import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Clock, IndianRupee, Filter, RefreshCw, Award } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS_STYLES = {
  pending:  "bg-amber-500/10  text-amber-600  border-amber-500/20",
  approved: "bg-blue-500/10   text-blue-600   border-blue-500/20",
  paid:     "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
  cancelled:"bg-red-500/10    text-red-600     border-red-500/20",
};
const LEVEL_LABELS = {
  national_partner: "National", regional_partner: "Regional",
  zonal_partner: "Zonal", sales_manager: "Manager", sales_partner: "Sales Partner",
};

export default function SMCommissionLedger() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [approving, setApproving] = useState(false);
  const [activeTab, setActiveTab] = useState("ledger"); // ledger | events | fund

  // Recognition Fund
  const [fund, setFund] = useState({ items: [], total_reserved: 0, total_allocated: 0 });

  const loadLedger = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50, ...(statusFilter && { status: statusFilter }) };
      const r = await axios.get(`${API}/api/sm/commission/ledger`, { params, withCredentials: true });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
      setPages(r.data.pages || 1);
    } catch { toast.error("Failed to load ledger"); }
    finally { setLoading(false); }
  }, [page, statusFilter]);

  const loadFund = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/sm/commission/recognition-fund`, { withCredentials: true });
      setFund(r.data);
    } catch {}
  }, []);

  useEffect(() => { loadLedger(); loadFund(); }, [loadLedger, loadFund]);

  const handleApprove = async (id) => {
    try {
      await axios.post(`${API}/api/sm/commission/ledger/${id}/approve`, {}, { withCredentials: true });
      toast.success("Entry approved & GL posted");
      loadLedger();
    } catch (err) { toast.error(err?.response?.data?.detail || "Approval failed"); }
  };

  const handleBulkApprove = async () => {
    if (selected.size === 0) return;
    setApproving(true);
    try {
      const r = await axios.post(`${API}/api/sm/commission/ledger/bulk-approve`,
        { entry_ids: [...selected] }, { withCredentials: true });
      toast.success(`${r.data.approved} entries approved`);
      setSelected(new Set());
      loadLedger();
    } catch { toast.error("Bulk approval failed"); }
    finally { setApproving(false); }
  };

  const toggleSelect = (id) => setSelected(s => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const pendingItems = items.filter(i => i.status === "pending");
  const allPendingSelected = pendingItems.length > 0 && pendingItems.every(i => selected.has(i.id));

  const selectAllPending = () => {
    if (allPendingSelected) setSelected(new Set());
    else setSelected(new Set(pendingItems.map(i => i.id)));
  };

  return (
    <div className="p-4 sm:p-6 space-y-5" data-testid="sm-commission-ledger">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-foreground">Commission Ledger</h1>
          <p className="text-sm text-muted-foreground mt-0.5">{total} entries across all partners</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {selected.size > 0 && (
            <button onClick={handleBulkApprove} disabled={approving}
              data-testid="bulk-approve-btn"
              className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-xl text-[13px] font-medium hover:opacity-90 disabled:opacity-50 transition-all whitespace-nowrap">
              <CheckCircle2 className="w-4 h-4" />
              {approving ? "Approving…" : `Approve ${selected.size} Selected`}
            </button>
          )}
          <button onClick={() => { loadLedger(); loadFund(); }}
            className="p-2 border border-border rounded-xl text-muted-foreground hover:text-foreground hover:bg-muted transition-all shrink-0">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex bg-muted rounded-xl p-1 gap-1 w-fit">
        {[{ key: "ledger", label: "Ledger", icon: IndianRupee },
          { key: "fund", label: "Recognition Fund", icon: Award }].map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            data-testid={`tab-${t.key}`}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-[12px] font-medium transition-all ${
              activeTab === t.key ? "bg-card shadow text-foreground" : "text-muted-foreground"}`}>
            <t.icon className="w-3.5 h-3.5" />{t.label}
          </button>
        ))}
      </div>

      {activeTab === "ledger" && (
        <>
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-2">
            <Filter className="w-4 h-4 text-muted-foreground shrink-0" />
            {["", "pending", "approved", "paid"].map(s => (
              <button key={s} onClick={() => { setStatusFilter(s); setPage(1); }}
                className={`text-[11px] uppercase tracking-[0.16em] px-3 py-1.5 rounded-lg border transition-all whitespace-nowrap ${
                  statusFilter === s ? "bg-primary/10 text-primary border-primary/30" : "border-border text-muted-foreground hover:bg-muted"}`}>
                {s || "All"}
              </button>
            ))}
            {pendingItems.length > 0 && (
              <button onClick={selectAllPending}
                className="text-[11px] uppercase tracking-[0.16em] px-3 py-1.5 rounded-lg border border-amber-500/30 text-amber-600 hover:bg-amber-500/5 transition-all whitespace-nowrap sm:ml-auto">
                {allPendingSelected ? "Deselect All Pending" : `Select All Pending (${pendingItems.length})`}
              </button>
            )}
          </div>

          {/* Table */}
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
            <table className="w-full text-[12px] min-w-[780px]">
              <thead className="border-b border-border bg-muted/20">
                <tr>
                  <th className="w-8 px-3 py-3" />
                  {["Partner", "Level", "Base Amount", "Rate", "Gross Comm.", "TDS", "Net Comm.", "Status", "Action"].map(h => (
                    <th key={h} className="px-3 py-3 text-left text-[9px] uppercase tracking-[0.18em] text-muted-foreground font-medium whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={10} className="py-10 text-center text-muted-foreground animate-pulse">Loading…</td></tr>
                )}
                {!loading && items.map(item => (
                  <tr key={item.id} className={`border-b border-border/50 hover:bg-muted/20 transition-colors ${selected.has(item.id) ? "bg-primary/5" : ""}`}>
                    <td className="px-3 py-3">
                      {item.status === "pending" && (
                        <input type="checkbox" checked={selected.has(item.id)} onChange={() => toggleSelect(item.id)}
                          className="accent-primary w-3.5 h-3.5" />
                      )}
                    </td>
                    <td className="px-3 py-3">
                      <div className="font-medium text-foreground whitespace-nowrap">{item.partner_name}</div>
                      <div className="text-muted-foreground text-[10px]">{item.partner_email}</div>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                        {LEVEL_LABELS[item.partner_type] || item.partner_type}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-foreground whitespace-nowrap">{fmt(item.base_amount)}</td>
                    <td className="px-3 py-3 text-muted-foreground whitespace-nowrap">{item.rate_pct}%</td>
                    <td className="px-3 py-3 text-foreground font-medium whitespace-nowrap">{fmt(item.gross_commission)}</td>
                    <td className="px-3 py-3 text-red-500 whitespace-nowrap">−{fmt(item.tds_amount)} <span className="text-muted-foreground text-[10px]">({item.tds_rate}%)</span></td>
                    <td className="px-3 py-3 text-emerald-600 font-semibold whitespace-nowrap">{fmt(item.net_commission)}</td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <span className={`text-[9px] uppercase tracking-[0.16em] border rounded-full px-2 py-0.5 font-semibold ${STATUS_STYLES[item.status]}`}>
                        {item.status}
                      </span>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      {item.status === "pending" && (
                        <button onClick={() => handleApprove(item.id)}
                          data-testid={`approve-${item.id}`}
                          className="text-[10px] uppercase tracking-[0.14em] px-2.5 py-1 border border-emerald-500/30 text-emerald-600 rounded-lg hover:bg-emerald-500/10 transition-all">
                          Approve
                        </button>
                      )}
                      {item.gl_journal_id && (
                        <span className="text-[9px] text-muted-foreground">GL ✓</span>
                      )}
                    </td>
                  </tr>
                ))}
                {!loading && items.length === 0 && (
                  <tr><td colSpan={10} className="py-12 text-center text-muted-foreground text-sm">
                    {statusFilter ? `No ${statusFilter} entries` : "No commission entries yet. Trigger a subscription event to generate commissions."}
                  </td></tr>
                )}
              </tbody>
            </table>
            </div>
          </div>

          {/* Pagination */}
          {pages > 1 && (
            <div className="flex items-center justify-between text-[12px] text-muted-foreground">
              <span>{total} total entries</span>
              <div className="flex items-center gap-1">
                <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                  className="px-3 py-1.5 border border-border rounded-lg disabled:opacity-40 hover:bg-muted transition-all">Prev</button>
                <span className="px-3">{page} / {pages}</span>
                <button disabled={page >= pages} onClick={() => setPage(p => p + 1)}
                  className="px-3 py-1.5 border border-border rounded-lg disabled:opacity-40 hover:bg-muted transition-all">Next</button>
              </div>
            </div>
          )}
        </>
      )}

      {activeTab === "fund" && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-card border border-border rounded-xl p-5">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">Reserved Pool</div>
              <div className="text-2xl font-bold text-amber-600">{fmt(fund.total_reserved)}</div>
            </div>
            <div className="bg-card border border-border rounded-xl p-5">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">Allocated / Paid</div>
              <div className="text-2xl font-bold text-emerald-600">{fmt(fund.total_allocated)}</div>
            </div>
          </div>
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="px-5 py-3.5 border-b border-border text-[13px] font-semibold">Recognition Fund Entries</div>
            <table className="w-full text-[12px]">
              <thead className="border-b border-border bg-muted/20">
                <tr>
                  {["Event ID", "Amount", "Status", "Allocated To", "Reason", "Action"].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-[9px] uppercase tracking-[0.16em] text-muted-foreground">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {fund.items.map(f => (
                  <tr key={f.id} className="border-b border-border/50 hover:bg-muted/20">
                    <td className="px-4 py-3 font-mono text-muted-foreground text-[10px]">{f.event_id?.slice(-8)}</td>
                    <td className="px-4 py-3 font-semibold text-amber-600">{fmt(f.amount)}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[9px] uppercase tracking-[0.14em] border rounded-full px-2 py-0.5 font-semibold ${
                        f.status === "reserved" ? "bg-amber-500/10 text-amber-600 border-amber-500/20" :
                        f.status === "allocated" ? "bg-blue-500/10 text-blue-600 border-blue-500/20" :
                        "bg-emerald-500/10 text-emerald-600 border-emerald-500/20"}`}>{f.status}</span>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{f.allocated_to_partner_name || "—"}</td>
                    <td className="px-4 py-3 text-muted-foreground">{f.allocation_reason || "—"}</td>
                    <td className="px-4 py-3">
                      {f.status === "reserved" && (
                        <span className="text-[10px] text-muted-foreground">Allocate via admin action</span>
                      )}
                    </td>
                  </tr>
                ))}
                {fund.items.length === 0 && (
                  <tr><td colSpan={6} className="py-10 text-center text-muted-foreground text-sm">No recognition fund entries yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
